import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MapPin } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ASSETS } from "@/lib/data";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.25 + i * 0.12 },
  }),
};

const MaskLine = ({ children, i, className = "" }) => (
  <span className="block overflow-hidden">
    <motion.span variants={line} initial="hidden" animate="show" custom={i} className={`block ${className}`}>
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const navigate = useNavigate();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.15, 1.35]);
  const logoY = useTransform(scrollYProgress, [0, 1], ["0%", "120%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section id="top" ref={ref} className="relative flex min-h-[100svh] items-center justify-center overflow-hidden">
      {/* Parallax background image */}
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
        <img src={ASSETS.heroAtmosphere} alt="Pahlawan arena" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-void/70 via-void/50 to-void" />
        <div className="absolute inset-0 bg-void/40" />
      </motion.div>

      {/* Content */}
      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto max-w-6xl px-4 text-center sm:px-8">
        <motion.div style={{ y: logoY }} className="mb-6 flex justify-center">
          <motion.img
            initial={{ opacity: 0, scale: 0.85, filter: "blur(12px)" }}
            animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
            transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
            src={ASSETS.heroLogo}
            alt="Pahlawan Championship"
            className="h-auto w-[260px] object-contain sm:w-[560px] lg:w-[720px]"
            style={{ filter: "drop-shadow(0 0 40px rgba(255,0,216,0.35))" }}
            data-testid="hero-logo"
          />
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.15 }}
          className="font-mono-cap mb-5 text-[11px] text-neon-cyan"
        >
          Malaysia&apos;s Home Of Muay Thai
          <br />
          Est. 2009
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0, duration: 0.7 }}
          className="mx-auto mt-6 max-w-2xl font-body text-base text-slate-300 sm:text-lg"
        >
          World-class Muay Thai action, live. Where courage, discipline, and the warrior spirit meet under the lights.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.15, duration: 0.7 }}
          className="mt-9 flex flex-col items-center justify-center gap-4 sm:flex-row"
        >
          <button
            onClick={() => navigate("/tickets")}
            className="group relative w-full overflow-hidden rounded-full bg-gradient-to-r from-neon-pink to-neon-violet px-8 py-4 font-mono-cap text-[12px] text-white sm:w-auto"
            data-testid="hero-cta-tickets"
          >
            <span className="relative z-10">Buy Ticket</span>
          </button>
          <div className="flex items-center justify-center gap-2 text-center font-mono-cap text-[10px] text-slate-200 sm:text-[11px]">
            <MapPin className="h-4 w-4 shrink-0 text-neon-cyan" />
            28 November 2026 · Stadium Titiwangsa, KL
          </div>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute inset-x-0 bottom-6 z-10 flex justify-center"
      >
        <ArrowDown className="h-5 w-5 animate-bounce text-neon-cyan" />
      </motion.div>
    </section>
  );
};
